<template>
    <div class="flex-grow-1">
        <div v-if="item.is_restricted" class="alert alert-danger">
            <div><span class="h5">Restricted Item:</span> Please contact Dave Hall, Kris Gillespie or Mark Eichelberger to add this item.</div>
            <div><span class="h5">Description of restriction:</span> {{ item.restricted_description }}</div>
        </div>
        <div class="d-flex align-items-center">
            <div v-if="bulk" class="mr-3">
                <b-form-checkbox
                    :checked="selected || itemLinked(item)"
                    :disabled="selectionDisabled"
                    @change="toggleSelected"
                >
                    <span class="sr-only">Select media item</span>
                </b-form-checkbox>
            </div>
            <div class="mr-3">
                <div class="thumbnail">
                    <b-link :href="thumbnail" target="_blank">
                        <b-img
                            :src="thumbnail"
                            class="p-0"
                            width="100"
                            height="auto"
                            thumbnail
                        />
                    </b-link>
                </div>
            </div>
            <div class="d-flex flex-column mr-3">
                <div>
                    <div v-if="item.id" class="d-flex">
                        <div class="mr-1">
                            Media ID:
                        </div>
                        <div class="item-description">
                            <strong>{{item.id}}</strong>
                        </div>
                    </div>
                    <div v-if="item.id" class="d-flex">
                        <div class="mr-1">
                            Merlin IDs:
                        </div>
                        <div class="item-description">
                            <strong>{{(item.merlin_ids || []).join(', ')}}</strong>
                        </div>
                    </div>
                    <div class="d-flex">
                        <div class="mr-1">
                            Title:
                        </div>
                        <div>
                            <span v-if="item.title">
                                <strong>{{item.title}}</strong>
                            </span>
                            <span v-else class="text-muted">
                                no title
                            </span>
                        </div>
                    </div>
                    <div class="d-flex">
                        <div class="mr-1">
                            Original File Name:
                        </div>
                        <div>
                            <span v-if="item.merlin_title">
                                <strong>{{item.merlin_title}}</strong>
                            </span>
                            <span v-else class="text-muted">
                                no original file name
                            </span>
                        </div>
                    </div>
                    <div v-if="item.description" class="d-flex">
                        <div class="mr-1">
                            Description:
                        </div>
                        <div class="item-description">
                            <strong>{{item.description}}</strong>
                        </div>
                    </div>
                    <div v-if="item.alt" class="d-flex">
                        <div class="mr-1">
                            Alt:
                        </div>                        
                        <strong>{{item.alt}}</strong>
                    </div>
                    <div v-if="item.type" class="d-flex">
                        <div class="mr-1">
                            Type:
                        </div>
                        <div>
                            <b-badge variant="secondary" class="font-weight-bold">
                                <div class="d-flex align-items-center">
                                    <div class="mr-1">
                                        <font-awesome-icon :icon="['fad', 'volume']" v-if="media_type === 'audio'" fixed-width />
                                        <font-awesome-icon :icon="['fad', 'image']" v-if="media_type === 'image'" fixed-width />
                                        <font-awesome-icon :icon="['fad', 'video']" v-if="media_type === 'video'" fixed-width />
                                        <font-awesome-icon :icon="['fad', 'code']" v-if="media_type === 'sims'" fixed-width />
                                    </div>
                                    <div class="text-capitalize">
                                        {{item.type}}
                                    </div>
                                </div>
                            </b-badge>
                        </div>
                    </div>
                    <div v-if="item.url" class="d-flex">
                        <div class="mr-1">
                            Source:
                        </div>
                        <div>
                            <strong>
                                <a :href="item.url" target="_blank" class="item-url">{{item.url}}</a>
                            </strong>
                        </div>
                    </div>
                    <div v-if="item.language" class="d-flex">
                        <div class="mr-1">
                            Language:
                        </div>
                        <div>
                            <strong>
                                <div class="d-flex align-items-center">
                                    <div v-if="item.language === 'en' || item.language === 'us'">
                                        <flag iso="us" />
                                    </div>
                                    <div v-if="item.language === 'es' || item.language === 'sp'">
                                        <flag iso="es" />
                                    </div>
                                    <div class="ml-1">{{item.language}}</div>
                                </div>
                            </strong>
                        </div>
                    </div>
                    <div v-if="type === 'image'" class="d-flex align-items-center mb-1">
                        <div class="label mr-1">
                            Width:
                        </div>
                        <div>
                            <b-form-group class="p-1 pt-3 text-center">
                                <b-form-select
                                    v-model="width"
                                    :options="width_options"
                                ></b-form-select>
                            </b-form-group>
                        </div>
                    </div>
                    <div v-if="type === 'image'" class="d-flex align-items-center">
                        <div class="label mr-1">
                            Align:
                        </div>
                        <div>
                            <b-form-select size="sm" v-model="position" style="width: 100px;" @keydown.enter.prevent>
                                <b-form-select-option value="left">
                                    Left
                                </b-form-select-option>
                                <b-form-select-option value="right">
                                    Right
                                </b-form-select-option>
                                <b-form-select-option value="behind">
                                   Center Behind Text
                                </b-form-select-option>
                                <b-form-select-option value="front">
                                    Center In Front of Text
                                </b-form-select-option>
                                <b-form-select-option value="topandbottom">
                                    Top and Bottom
                                </b-form-select-option>
                            </b-form-select>
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-if="(!bulk && (!item.is_restricted || can_attach_restricted_media)) || itemLinked(item) || item.linking"
                class="ml-auto"
            >
                <div class="text-nowrap">
                    <span v-if="itemLinked(item)">
                        <b-link class="text-warning" href="#" @click.prevent="unlinkItem(item)">
                            <font-awesome-icon :icon="['fad', 'unlink']" />
                        </b-link>
                    </span>
                    <span v-else>
                        <span v-if="item.linking">
                            <b-spinner small label="Loading..."></b-spinner>
                        </span>
                        <span v-else-if="!bulk">
                            <b-link class="text-primary" href="#" @click.prevent="validateFields(item)">
                                <span v-if="article_related_media">
                                    <font-awesome-icon :icon="['fad', 'link']" />
                                </span>
                                <span v-else>
                                    <font-awesome-icon :icon="['fad', 'plus-square']" />
                                </span>
                            </b-link>
                        </span>
                    </span>
                </div>
            </div>
        </div>

        <edit-media-modal
            v-if="editing_media"
            v-model="show_edit_media_modal"
            :value="null"
            :editing_media="editing_media"
            :editing_media_pedigree_data="editing_media_pedigree_data"
            :alternate_media_prop="alternate_media_prop"
            @hideUploadMedia="show_edit_media_modal = false"
            @link="handleMediaUpdated"
        />

    </div>
</template>

<style lang="scss" scoped>
.thumbnail {
    width: 100px;
}

.item-url {
    word-break: break-all;
}

.item-description {
    max-height: 150px;
    overflow: scroll;
    word-break: break-word;
}
</style>

<script>
import EditMediaModal from '@/components/modals/media/EditMediaModal'

import { mapState } from 'vuex'

export default {
    data: () => ({
        show_edit_media_modal: false,

        editing_media: null,
        editing_media_pedigree_data: null,
        alternate_media_prop: null,
        width: 100,
        position: null,
        width_options: [ 10, 15, 20, 25, 30, 33, 40, 45, 50, 60, 66, 70, 75, 80, 90, 100 ],
    }),
    props: {
        publication: {},
        item: {},
        type: {},
        article_related_media: {},
        file_extensions: {},
        bulk: {
            type: Boolean,
            default: false
        },
        selected: {
            type: Boolean,
            default: false
        }
    },
    components: {
        EditMediaModal
    },
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        can_attach_restricted_media() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'attach_restricted_media')
        },
        selectionDisabled () {
            return this.itemLinked(this.item) ||
                this.item.linking ||
                (this.item.is_restricted && !this.can_attach_restricted_media)
        },
        media_type () {
            let media_type = null

            if (
                this.item &&
                this.item.type
            ) {
                media_type = this.item.type.toLowerCase()
            }

            return media_type
        },
        thumbnail () {
            let thumbnail = null

            if (
                this.item &&
                this.item.splash_url
            ) {
                thumbnail = this.item.splash_url
            } else {
                thumbnail = this.item.url
            }

            return thumbnail
        }
    },
    methods: {
        async toggleSelected (selected) {
            if (!selected) {
                this.$emit('deselect', this.item)
                return
            }

            const valid = await this.validateFields(this.item, false)

            if (valid) {
                this.$emit('select', this.item)
            }
        },
        async addSelectedItem () {
            const valid = await this.validateFields(this.item, false)

            if (!valid) {
                return false
            }

            return {
                item: this.item,
                width: this.width,
                position: this.position
            }
        },
        handleMediaUpdated (item) {
            if (!this.bulk) {
                this.linkItem(item)
                return
            }

            _.assign(this.item, item)

            if (this.item.alt) {
                this.$emit('select', this.item)
            } else {
                this.$emit('deselect', this.item)
            }

            this.show_edit_media_modal = false
        },
        async validateFields(item, link_item = true) {
            if(item.is_restricted){
                const result = await this.$swal({
                    html: 'This Media has a restriction. Do you wish to continue?',
                    showCancelButton: true,
                    confirmButtonText: 'Add Restricted Media',
                    confirmButtonColor: '#E67E22',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                })

                if (!result.value) {
                    return false;
                }
            }
            // pop edit media modal if image has no alt tag
            if(this.media_type == 'image' && !item.alt) {
                this.$bvToast.toast('This image is missing an Alt tag, please update the image with an alt tag', {
                    title: 'Alt Field Missing',
                    variant: 'danger',
                    solid: false,
                    autoHideDelay: 4000,
                    appendToast: true,
                    toaster: 'b-toaster-top-center'
                })
                this.openEditMediaModal(item)
                return false
            } else {
                if (link_item) {
                    this.linkItem(item)
                }

                return true
            }
        },
        linkItem (item) {  
            this.$emit('link', item, this.width, this.position)
        },
        unlinkItem (item) {
            this.$emit('unlink', item)
        },
        itemLinked (item) {
            let itemLinked = false
            if (
                this.article_related_media &&
                this.article_related_media.length
            ) {
                _.forEach(this.article_related_media, media_item => {
                    if (
                        media_item.pivot &&
                        media_item.pivot.publication_id &&
                        media_item.pivot.publication_id == this.publication.id
                    ) {
                        if (item.dam_id && (item.dam_id == media_item.dam_id)) {
                            itemLinked = true
                        } else if (item.media_id && (item.media_id == media_item.media_id)) {
                            itemLinked = true
                        } else if (item.id == media_item.id) {
                            itemLinked = true
                        }
                    }
                })
            }

            return itemLinked
        },
        openEditMediaModal(edit_media){
            this.$store.commit('setSelectedMedia', Object.assign({}, edit_media));
            this.editing_media = Object.assign({}, edit_media);
            if (Array.isArray(this.editing_media.merlin_ids)) {
                this.editing_media.merlin_ids = this.editing_media.merlin_ids.join(', ')
            }
            this.show_edit_media_modal = true;
        },
    }
}
</script>
